import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import type { DailyEntryOptions } from "@crewledger/shared/schemas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { searchDailyEntryWorkers } from "./api";

interface AddWorkerModalProps {
  open: boolean;
  siteId: string;
  excludeWorkerIds: string[];
  onClose: () => void;
  onSelect: (worker: DailyEntryOptions["workers"][number]) => void;
}

export function AddWorkerModal({
  open,
  siteId,
  excludeWorkerIds,
  onClose,
  onSelect,
}: AddWorkerModalProps) {
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);
  const [workers, setWorkers] = useState<DailyEntryOptions["workers"]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !siteId) return;
    setLoading(true);
    setError(null);
    searchDailyEntryWorkers({
      siteId,
      page: 1,
      pageSize: 25,
      q: debouncedQ || undefined,
      excludeWorkerIds,
    })
      .then((res) => { setWorkers(res.data); })
      .catch((err: unknown) =>
        { setError(err instanceof Error ? err.message : "Search failed"); },
      )
      .finally(() => { setLoading(false); });
  }, [open, siteId, debouncedQ, excludeWorkerIds]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl border bg-card-white p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-navy-deep">Add worker</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Search active workers assigned to this site.
        </p>

        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            placeholder="Employee ID or name…"
            value={q}
            onChange={(e) => { setQ(e.target.value); }}
            className="pl-9"
          />
        </div>

        <div className="mt-4 max-h-72 overflow-y-auto rounded-lg border">
          {loading ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">Searching…</p>
          ) : error ? (
            <p className="px-4 py-8 text-center text-sm text-red-600">{error}</p>
          ) : workers.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              No workers match your search.
            </p>
          ) : (
            <ul>
              {workers.map((worker) => (
                <li key={worker.id} className="border-b last:border-0">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-bg-light/70"
                    onClick={() => {
                      onSelect(worker);
                      onClose();
                    }}
                  >
                    <span>
                      <span className="font-medium text-navy-deep">{worker.displayName}</span>
                      <span className="ml-2 font-mono text-xs text-muted-foreground">
                        {worker.employeeId}
                      </span>
                    </span>
                    {worker.crew ? (
                      <span className="text-xs text-muted-foreground">{worker.crew}</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
